/*
 * Oorto self-contained wishlist.
 * Stores wishlisted products in localStorage (guest-friendly, no app needed) and
 * powers the wishlist toggle buttons, the header count badge and the wishlist page.
 */
(function () {
  'use strict';

  if (window.__oortoWishlistInit) return;
  window.__oortoWishlistInit = true;

  var STORAGE_KEY = 'oorto:wishlist';
  var CHANGE_EVENT = 'oorto:wishlist:change';

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (entry) {
        return entry && typeof entry.handle === 'string' && entry.handle.length > 0;
      });
    } catch (error) {
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      /* storage may be unavailable (private mode); fail silently */
    }
  }

  function emit() {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { items: read() } })
    );
  }

  var OortoWishlist = {
    get: function () {
      return read();
    },
    handles: function () {
      return read().map(function (entry) {
        return entry.handle;
      });
    },
    count: function () {
      return read().length;
    },
    has: function (handle) {
      if (!handle) return false;
      return read().some(function (entry) {
        return entry.handle === handle;
      });
    },
    add: function (handle, id) {
      if (!handle || this.has(handle)) return;
      var items = read();
      items.unshift({ handle: handle, id: id ? String(id) : '', added_at: Date.now() });
      write(items);
      emit();
    },
    remove: function (handle) {
      if (!handle) return;
      var items = read().filter(function (entry) {
        return entry.handle !== handle;
      });
      write(items);
      emit();
    },
    toggle: function (handle, id) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle, id);
      return true;
    }
  };

  window.OortoWishlist = OortoWishlist;

  /* ----------------------------------------------------------------------- */
  /* Toggle buttons                                                          */
  /* ----------------------------------------------------------------------- */

  function reflectButton(button) {
    var handle = button.getAttribute('data-product-handle');
    var active = OortoWishlist.has(handle);
    button.classList.toggle('is-wishlisted', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    var addLabel = button.getAttribute('data-label-add');
    var removeLabel = button.getAttribute('data-label-remove');
    if (addLabel && removeLabel) {
      button.setAttribute('aria-label', active ? removeLabel : addLabel);
    }
  }

  function reflectAllButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-toggle]');
    Array.prototype.forEach.call(buttons, reflectButton);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    var handle = button.getAttribute('data-product-handle');
    var id = button.getAttribute('data-product-id');
    if (!handle) return;
    OortoWishlist.toggle(handle, id);
  });

  /* ----------------------------------------------------------------------- */
  /* Header count badge                                                      */
  /* ----------------------------------------------------------------------- */

  function reflectCounts() {
    var count = OortoWishlist.count();
    var nodes = document.querySelectorAll('[data-wishlist-count]');
    Array.prototype.forEach.call(nodes, function (node) {
      node.textContent = count > 99 ? '99+' : String(count);
      node.toggleAttribute('hidden', count === 0);
    });
  }

  /* ----------------------------------------------------------------------- */
  /* Wishlist page                                                           */
  /* ----------------------------------------------------------------------- */

  var WishlistPage = (function () {
    function Page(root) {
      this.root = root;
      this.grid = root.querySelector('[data-wishlist-grid]');
      this.emptyState = root.querySelector('[data-wishlist-empty]');
      this.loading = root.querySelector('[data-wishlist-loading]');
      this.pagination = root.querySelector('[data-wishlist-pagination]');
      this.cardSection = root.getAttribute('data-card-section');
      this.perPage = parseInt(root.getAttribute('data-per-page'), 10) || 8;
      this.nextLabel = root.getAttribute('data-label-next') || 'Next';
      this.currentPage = 1;
      this.cache = {};
      this.renderId = 0;
      this.render();
    }

    Page.prototype.setLoading = function (isLoading) {
      if (this.loading) this.loading.toggleAttribute('hidden', !isLoading);
    };

    Page.prototype.fetchCard = function (handle) {
      var self = this;
      if (this.cache[handle]) return Promise.resolve(this.cache[handle]);
      var url = '/products/' + encodeURIComponent(handle) + '?section_id=' + this.cardSection;
      return fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Not found');
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var card = doc.querySelector('.oorto-wishlist-card');
          if (card) self.cache[handle] = card.outerHTML;
          return self.cache[handle] || '';
        })
        .catch(function () {
          return '';
        });
    };

    Page.prototype.render = function () {
      var self = this;
      var items = OortoWishlist.get();

      if (items.length === 0) {
        if (this.grid) this.grid.innerHTML = '';
        if (this.pagination) this.pagination.innerHTML = '';
        if (this.emptyState) this.emptyState.removeAttribute('hidden');
        this.setLoading(false);
        return;
      }

      if (this.emptyState) this.emptyState.setAttribute('hidden', '');

      var totalPages = Math.ceil(items.length / this.perPage);
      if (this.currentPage > totalPages) this.currentPage = totalPages;
      if (this.currentPage < 1) this.currentPage = 1;

      var start = (this.currentPage - 1) * this.perPage;
      var pageItems = items.slice(start, start + this.perPage);

      /* Guard against overlapping renders finishing out of order. */
      var token = ++this.renderId;

      this.setLoading(true);
      Promise.all(
        pageItems.map(function (entry) {
          return self.fetchCard(entry.handle);
        })
      )
        .then(function (cards) {
          if (token !== self.renderId) return;
          if (self.grid) self.grid.innerHTML = cards.join('');
          self.renderPagination(totalPages);
        })
        .catch(function () {
          if (token !== self.renderId) return;
          if (self.grid) self.grid.innerHTML = '';
        })
        .then(function () {
          if (token === self.renderId) self.setLoading(false);
        });
    };

    Page.prototype.renderPagination = function (totalPages) {
      if (!this.pagination) return;
      if (totalPages <= 1) {
        this.pagination.innerHTML = '';
        return;
      }
      var self = this;
      var frag = document.createDocumentFragment();

      for (var page = 1; page <= totalPages; page++) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oorto-wishlist__page-link';
        if (page === this.currentPage) btn.classList.add('is-active');
        btn.textContent = String(page);
        btn.setAttribute('data-page', String(page));
        frag.appendChild(btn);
      }

      if (this.currentPage < totalPages) {
        var next = document.createElement('button');
        next.type = 'button';
        next.className = 'oorto-wishlist__page-link oorto-wishlist__page-next';
        next.setAttribute('aria-label', this.nextLabel);
        next.setAttribute('data-page', String(this.currentPage + 1));
        next.innerHTML = '&rsaquo;';
        frag.appendChild(next);
      }

      this.pagination.innerHTML = '';
      this.pagination.appendChild(frag);

      Array.prototype.forEach.call(
        this.pagination.querySelectorAll('[data-page]'),
        function (node) {
          node.addEventListener('click', function () {
            self.currentPage = parseInt(node.getAttribute('data-page'), 10) || 1;
            self.render();
            self.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      );
    };

    return Page;
  })();

  /* ----------------------------------------------------------------------- */
  /* Wire up                                                                 */
  /* ----------------------------------------------------------------------- */

  var pages = [];

  function init() {
    reflectAllButtons();
    reflectCounts();
    var roots = document.querySelectorAll('[data-wishlist-page]');
    pages = Array.prototype.map.call(roots, function (root) {
      return new WishlistPage(root);
    });
  }

  document.addEventListener(CHANGE_EVENT, function () {
    reflectAllButtons();
    reflectCounts();
    pages.forEach(function (page) {
      page.render();
    });
  });

  /* Sync across tabs. */
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) emit();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
